import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
    useCreateAccountMutation,
    useResendEmailChangeAccountUserMutation,
} from '@graphql/generated-graphql';
import { Button, Checkbox, InputGroup } from '@components/base';
import { motion, useAnimation } from 'framer-motion';
import NextLink from 'next/link';
import { Wave } from '@components/common/svgs/WaveSVG';
import { ChevronRightIcon } from '@heroicons/react/solid';
import { useRouter } from 'next/router';
import { useMutation, useQueryClient } from 'react-query';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { getClient, refreshClient } from '@graphql/graphql-client/graphClient';
import { FacebookSVG } from '@components/common/svgs/FacbookSVG';
import { GoogleSVG } from '@components/common/svgs/GoogleSVG';
import { SnapchatSVG } from '@components/common/svgs/SnapchatSVG';
import { EyeconSVG } from '@components/common/svgs/EyeconSVG';
import { EyeconClosedSVG } from '@components/common/svgs/EyeconClosedSVG';
import { ErrorAnnouncement } from '@components/common/error/ErrorAnnouncement';
import { SkipperTextLogo } from '@components/common/header/SkipperTextLogo';
import { encodeQueryData } from '@utils/encodeQueryData';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { Input, LabelForInput, TitleAndTags } from '@skipper/ui';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const FormValidationSchema = yup.object().shape({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    email: yup.string().required('Email is required'),
    password: yup.string().required('Password is required'),
});

export default function Signup() {
    const [showSignUpForm, setShowSignUpForm] = useState(true);
    const [email, setEmail] = useState('');
    const [userId, setUserId] = useState('');

    return (
        <div className="h-screen overflow-y-auto">
            <TitleAndTags title="Signup" />
            <header className="pt-10 px-10">
                <div className="flex items-center justify-between px-20">
                    <SkipperTextLogo href="https://skipper.io" />
                </div>
            </header>
            <div className="relative pt-10 h-full ">
                {showSignUpForm && !email && !userId ? (
                    <SignupForm
                        setShowSignUpForm={setShowSignUpForm}
                        setEmail={setEmail}
                        setUserId={setUserId}
                    />
                ) : (
                    <VerifyEmail
                        email={email}
                        userId={userId}
                        setShowSignUpForm={setShowSignUpForm}
                        setEmail={setEmail}
                        setUserId={setUserId}
                    />
                )}
                <Wave />
            </div>
        </div>
    );
}

const SignupForm = ({
    setShowSignUpForm,
    setEmail,
    setUserId,
}: {
    setShowSignUpForm: React.Dispatch<React.SetStateAction<Boolean>>;
    setEmail: React.Dispatch<React.SetStateAction<string>>;
    setUserId: React.Dispatch<React.SetStateAction<string>>;
}) => {
    const { dispatchNotification } = useUniversalModalDispatch();
    const { register, handleSubmit, control } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            password: '',
        },
    });
    const [showPassword, setShowPassword] = useState(false);
    const [checkboxState, setCheckboxState] = useState(false);
    const [emailErrorsArray, setEmailErrorsArray] = useState<string[]>([]);
    const [passwordErrorsArray, setPasswordErrorsArray] = useState<string[]>([]);
    const initialValues = useMemo(() => {
        return { firstName: '', lastName: '', email: '', password: '' };
    }, []);
    const createAccountMutation = useCreateAccountMutation(getClient('users'), {
        onSuccess: (data) => {
            if (data?.createAccount?.success) {
                dispatchNotification({
                    title: 'Account created successfully',
                    text: '',
                    type: 'success',
                });
                setEmail(data?.createAccount?.account?.users?.[0]?.email ?? '');
                setUserId(data?.createAccount?.account?.users?.[0]?.id ?? '');
                setShowSignUpForm(false);
                // router.back();
            } else {
                if (!!data?.createAccount?.errors?.length) {
                    setEmailErrorsArray(
                        data?.createAccount?.errors?.filter((e) =>
                            e.toLocaleLowerCase().includes('email'),
                        ),
                    );
                    setPasswordErrorsArray(
                        data?.createAccount?.errors?.filter((e) =>
                            e.toLocaleLowerCase().includes('password'),
                        ),
                    );
                }
                dispatchNotification({
                    title: 'Error creating account',
                    text:
                        data?.createAccount?.errors?.[0] ??
                        'Something went wrong. Please try again later.',
                    type: 'danger',
                });
            }
        },
    });
    const successCallback = useCallback(
        (vals: any) => {
            createAccountMutation.mutate({
                firstName: vals.firstName,
                lastName: vals.lastName,
                email: vals.email,
                password: vals.password,
            });
        },
        [createAccountMutation],
    );

    const errorCallback = useCallback((errors) => {
        if (errors) {
            dispatchNotification({
                title: 'Error',
                text: 'Please fill out the required fields',
                type: 'danger',
            });
        }
    }, []);

    const methods = useForm<any>({
        defaultValues: initialValues as typeof initialValues,
        resolver: yupResolver(FormValidationSchema),
        mode: 'all',
    });

    const { errors } = useFormState({ control });
    const container = useRef<HTMLDivElement | null>(null);
    const Eyecon = (
        <div
            className="absolute right-0 top-0 bottom-0 flex items-center pr-4 cursor-pointer"
            onClick={() => setShowPassword((s) => !s)}>
            {showPassword ? (
                <EyeconSVG className="fill-current text-gray-800" />
            ) : (
                <EyeconClosedSVG className="absolute right-0 top-0 bottom-0" />
            )}
        </div>
    );
    return (
        <>
            <div className="z-10">
                <div className="flex justify-center">
                    <div className="px-10 lg:px-20 py-12 bg-white rounded-sm z-10 shadow-md border border-gray-100 lg:w-[50%] min-w-[400px] max-w-[700px]">
                        <div className="lg:px-10 md:text-4xl text-xl text-passport font-inter font-bold mb-4 text-center">
                            <p>Sign-up for a</p>
                            <p className="md:mt-5">Skipper account</p>
                        </div>
                        <FormProvider {...methods}>
                            <div className="mt-6 flex flex-col items-start gap-y-1">
                                <>
                                    <LabelForInput htmlFor="firstName">First name</LabelForInput>
                                    <Input
                                        id="firstName"
                                        name="firstName"
                                        showErrorOutline={!!errors?.firstName}
                                        {...register('firstName', {required: true})}
                                    />

                                    {!!errors?.firstName && (
                                        <p className="text-red-500 mt-1 text-xs">
                                            This field is required
                                        </p>
                                    )}
                                </>
                                <>
                                    <LabelForInput htmlFor="lastName" className="mt-2">
                                        Last name
                                    </LabelForInput>
                                    <Input
                                        id="lastName"
                                        name="lastName"
                                        showErrorOutline={!!errors?.lastName}
                                        {...register('lastName', {required: true})}
                                    />
                                    {errors?.lastName && (
                                        <p className="text-red-500 mt-1 text-xs">
                                            This field is required
                                        </p>
                                    )}
                                </>
                                <>
                                    <LabelForInput htmlFor="email" className="mt-2">
                                        Email
                                    </LabelForInput>
                                    <Input
                                        id="email"
                                        name="email"
                                        showErrorOutline={
                                            !!errors?.email || !!emailErrorsArray.length
                                        }
                                        {...register('email', {required: true})}
                                    />
                                    {errors?.email && (
                                        <p className="text-red-500 mt-1 text-xs">
                                            This field is required
                                        </p>
                                    )}
                                    {!!emailErrorsArray.length && (
                                        <>
                                            {emailErrorsArray?.map((e, index) => (
                                                <p
                                                    key={`email_error_${index}`}
                                                    className="text-red-500 mt-1 text-xs">
                                                    {e}
                                                </p>
                                            ))}
                                        </>
                                    )}
                                </>
                                <>
                                    <LabelForInput htmlFor="password" className="mt-2">
                                        Password
                                    </LabelForInput>
                                    <div className="relative w-full">
                                        <Input
                                            type={`${showPassword ? 'text' : 'password'}`}
                                            id="password"
                                            name="password"
                                            showErrorOutline={
                                                !!errors?.password || !!passwordErrorsArray.length
                                            }
                                            {...register('password', {required: true})}
                                        />
                                        {Eyecon}
                                    </div>
                                    {errors?.password && (
                                        <p className="text-red-500 mt-1 text-xs">
                                            This field is required
                                        </p>
                                    )}
                                    {!!passwordErrorsArray.length && (
                                        <>
                                            {passwordErrorsArray?.map((e, index) => (
                                                <p
                                                    key={`email_error_${index}`}
                                                    className="text-red-500 mt-1 text-xs">
                                                    {e}
                                                </p>
                                            ))}
                                        </>
                                    )}
                                    {!passwordErrorsArray.length && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            8 characters, including upper and lowercase letters and
                                            a digit
                                        </p>
                                    )}
                                </>
                            </div>
                            <div className="flex items-center mt-8">
                                <Checkbox
                                    setChecked={() => setCheckboxState((s) => !s)}
                                    checked={checkboxState}
                                    name="TermsAndPolicy"
                                />
                                <p className="text-sm text-passport">
                                    I agree with Skipper&apos;s{' '}
                                    <a
                                        href="https://skipper.io/terms"
                                        target="_blank"
                                        className="underline"
                                        rel="noreferrer">
                                        Terms of Service
                                    </a>{' '}
                                    and{' '}
                                    <a
                                        href="https://skipper.io/privacy"
                                        target="_blank"
                                        className="underline"
                                        rel="noreferrer">
                                        Privacy Policy
                                    </a>
                                    .
                                </p>
                            </div>
                            <div className="full text-center">
                                <Button
                                    variant="secondary"
                                    size="large"
                                    type="submit"
                                    shape="oval"
                                    className="text-center mt-8 mx-auto w-[50%] mx-auto"
                                    onClick={handleSubmit(successCallback, errorCallback)}
                                    disabled={createAccountMutation.isLoading || !checkboxState}>
                                    <p className="text-center mx-auto">{`${
                                        createAccountMutation.isLoading
                                            ? 'Signing up...'
                                            : 'Sign up'
                                    }`}</p>
                                </Button>
                            </div>
                        </FormProvider>
                    </div>
                </div>
            </div>
            <div className="relative z-20 w-full flex items-center justify-center mt-5">
                <div ref={container} className="group relative flex items-center gap-x-2">
                    <p className="text-base text-gray-500">Already have an account?</p>
                    <a
                        href="/auth/signin"
                        rel="noreferrer"
                        className="block z-20 text-teal-600 font-bold">
                        Log-in
                    </a>
                </div>
            </div>
            <div className="relative z-20 w-full flex items-center justify-center mt-5">
                <div ref={container} className="group relative flex items-center gap-x-2">
                    <p className="text-base text-gray-500">Did not get email confirmation? </p>
                    <a
                        href="/auth/email/resend"
                        rel="noreferrer"
                        className="block z-20 text-teal-600 font-bold">
                        Resend email
                    </a>
                </div>
            </div>
        </>
    );
};

const VerifyEmail = ({
                         email,
                         userId,
                         setShowSignUpForm,
                         setEmail,
                         setUserId,
                     }: {
    email: string;
    userId: string;
    setShowSignUpForm: React.Dispatch<React.SetStateAction<Boolean>>;
    setEmail: React.Dispatch<React.SetStateAction<string>>;
    setUserId: React.Dispatch<React.SetStateAction<string>>;
}) => {
    const {dispatchNotification} = useUniversalModalDispatch();

    return (
        <div className="z-10">
            <div className="flex justify-center">
                <div className="px-10 lg:px-20 py-12 bg-white rounded-sm z-10 shadow-md border border-gray-100 lg:w-[50%] min-w-[400px] max-w-[700px]">
                    <div className="lg:px-10 text-passport mb-4 text-center">
                        <p className="md:text-4xl text-xl font-bold">Verify your email</p>
                        <p className="text-sm leading-5 mt-8">
                            We sent a verification link to{' '}
                            {!!email ? <span className="font-bold">{email}</span> : 'your email'}
                        </p>
                        <p className="text-sm leading-5 mt-6">
                            Click on the link to complete the verification process. You may need to
                            check your spam or junk folder.
                        </p>
                        {/* <p
                            className="text-sm leading-5 mt-1 underline hover:text-teal-600 cursor-pointer"
                            onClick={() => {}}>
                            Resend email
                        </p> */}
                        <p className="text-sm leading-5 mt-6">
                            Need help? Contact support:{' '}
                            <span
                                className="text-info font-bold cursor-pointer hover:text-teal-600"
                                onClick={() => {
                                    navigator?.clipboard?.writeText('hello@skipper.io');
                                    dispatchNotification({
                                        title: 'Copied to clipboard',
                                        text: '',
                                        type: 'success',
                                    });
                                }}>
                                hello@skipper.io
                            </span>
                        </p>
                        <p
                            className="leading-5 mt-1 text-danger cursor-pointer mt-2"
                            onClick={() => {
                                setShowSignUpForm(true);
                                setEmail('');
                                setUserId('');
                            }}>
                            Back to sign-up
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
